import api from "../services/authService";

/**
 * AI document-analysis API client.
 *
 * Surfaces the document-briefing + attorney-analysis endpoints exposed by the
 * `documents` Django app. Backend lives in `documents/views/` (briefing,
 * analysis trigger, job-status, full-report). All calls flow through the
 * shared `authService` axios instance so JWT, CSRF, and 401-refresh behavior
 * stay consistent with the rest of the frontend.
 *
 * Conventions:
 *   - Functions return the raw axios response so callers can inspect
 *     `response.status` (notably 404 for "no briefing yet" empty states)
 *     instead of forcing every error into a thrown shape.
 *   - 404 is *not* a hard error for `getDocumentBriefing`; the slice treats
 *     it as a benign empty state. Other non-2xx responses propagate.
 */

/**
 * GET the latest auto-generated briefing for a document.
 * Returns 404 when the doc-pipeline hasn't produced a briefing yet —
 * callers should treat 404 as the empty state, not a failure.
 */
export const getDocumentBriefing = (documentId) => {
  if (!documentId) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/briefing/`, {
    // Don't throw on 404 — empty-state UI needs to render, not error out.
    validateStatus: (status) => status === 200 || status === 404,
  });
};

/**
 * POST a new attorney-analysis job for a document.
 * Returns immediately with `{ job_id, status: "running" }` (HTTP 202);
 * progress is polled via `getAnalysisJobStatus`.
 *
 * Optional `claimedOnly` toggles the `claimed_only` payload flag. The default
 * keeps The Brief focused on claimed, decided, or pending conditions; callers
 * can pass false only for the unusual "analyze every medical mention" path.
 *
 * Optional `queue` (BE PR #1898): when the user is at their concurrent-Brief
 * cap, the trigger normally 429s with the running Brief's progress and
 * `queueable: true`. Re-posting with `queue: true` creates the job as
 * `status: "queued"` instead (202 with `queue_position` + `running_brief`);
 * the backend promotes it to running when a slot frees.
 */
export const createAnalysisJob = (
  documentId,
  { claimedOnly = true, queue = false } = {}
) => {
  if (!documentId) {
    return Promise.reject(new Error("createAnalysisJob: documentId is required"));
  }
  return api.post(`/documents/${documentId}/analysis/`, {
    claimed_only: Boolean(claimedOnly),
    ...(queue ? { queue: true } : {}),
  });
};

/**
 * GET the list of analysis jobs for a document, newest-first.
 *
 * [DEPRECATED — prefer `listMatterAnalysisJobs`]
 *
 * Filters jobs by the document foreign key only, which means switching
 * to a sibling document inside the same matter returns an empty list
 * even though the analysis covers the whole claim file. The matter-
 * scoped endpoint mirrors the prototype's
 * `GET /api/clients/{client_id}/analysis-jobs?limit=10` and survives
 * doc switches. Kept here for any other consumer still wired to the
 * doc-scoped path; the AI panel now uses `listMatterAnalysisJobs`.
 */
export const listAnalysisJobs = (documentId) => {
  if (!documentId) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/analysis-jobs/`);
};

/**
 * GET the list of analysis jobs for a matter (whole claim file), newest-first.
 *
 * Mirrors the prototype's `GET /api/clients/{client_id}/analysis-jobs`
 * contract: the AI panel hydrates from this regardless of which document
 * inside the matter is currently open, so switching documents within a
 * matter no longer blanks out the analysis state.
 *
 * `limit` is clamped server-side to [1, 50] (default 10).
 */
export const listMatterAnalysisJobs = (matterId, { limit = 10 } = {}) => {
  if (!matterId) return Promise.resolve(null);
  const params = limit && limit !== 10 ? `?limit=${encodeURIComponent(limit)}` : "";
  return api.get(`/matters/${matterId}/analysis-jobs/${params}`);
};

/**
 * GET the lightweight status of a single analysis job.
 *
 * Backend returns the job's progress counters (`total_conditions`,
 * `completed_conditions`, `failed_conditions`) plus a `conditions[]`
 * array of `{ id, condition_name, condition_status, sequence_number,
 * status }` summaries — no analysis text. Designed for 5-second
 * polling: stays small even on long jobs with 20+ conditions.
 */
export const getAnalysisJobStatus = (jobId) => {
  if (!jobId) return Promise.resolve(null);
  return api.get(`/analysis-jobs/${jobId}/`);
};

/**
 * POST /api/analysis-jobs/<job_id>/cancel/
 *
 * Hard-stops a running Brief job: the backend marks it cancelled (and
 * any still pending/analyzing conditions failed with a "Cancelled by
 * user." note) and best-effort revokes + terminates the in-flight
 * Celery task so an in-progress LLM call actually stops rather than
 * only being noticed between conditions.
 *
 * Returns the updated job status payload (same shape as
 * `getAnalysisJobStatus`) on success. Backend returns 409 if the job
 * isn't currently running (already finished/failed/cancelled) —
 * callers should treat that as "nothing to do" rather than a hard
 * error.
 */
export const cancelAnalysisJob = (jobId) => {
  if (!jobId) {
    return Promise.reject(new Error("cancelAnalysisJob: jobId is required"));
  }
  return api.post(`/analysis-jobs/${jobId}/cancel/`);
};

/**
 * GET the full attorney-analysis report for a completed job.
 *
 * Returns the job plus `condition_analyses[]` (full
 * `analysis_text` markdown + `condition_metadata` for the
 * Phase 4 chips) and `discovery_data` (which carries
 * `strategic_narrative` for the Strategic Overview card).
 *
 * Only meaningful once the job's status is `completed` or
 * `completed_with_errors` — backend will return the row for
 * any job, but `analysis_text` is empty until per-condition
 * workers finish, so the slice gates this fetch on terminal
 * status.
 *
 * The payload can be in the 50–500 KB range on a 10-condition
 * matter (each condition holds 5–30 KB of markdown), so we
 * fetch lazily — only when the user opens the Analysis tab on
 * a terminal job, never as part of the 5s poll.
 */
export const getAnalysisJobReport = (jobId) => {
  if (!jobId) return Promise.resolve(null);
  return api.get(`/analysis-jobs/${jobId}/report/`);
};

/**
 * POST /api/matters/<matter_id>/save-brief/
 *
 * Promote a completed AnalysisJob to a real DMS Document (PDF) under
 * the matter's auto-created `AI Briefs` folder. Re-saves bump the
 * version of the same Document instead of creating a new row, so any
 * DocumentShare links / open tabs / "last opened" pointers stay live
 * across re-runs of the Brief.
 *
 * Backend contract (BE PR #327):
 *   - 201 on first save, 200 on re-save.
 *   - Body: `{ document, folder, created, version }` where document
 *     and folder are the standard serialized shapes from the documents
 *     app, `created` is true on first save, and `version` is the
 *     current SharePoint version number (1 on first save, N on Nth
 *     re-save).
 *   - 404 when matter / job is missing or job belongs to a different
 *     matter (intentionally indistinguishable so we don't leak job
 *     existence to a caller without matter access).
 *   - 422 when the job has no usable report content (e.g. cancelled
 *     before any condition completed).
 *   - 500 on PDF render or storage upload failure with `detail`
 *     carrying the underlying error message.
 *
 * Returns the raw axios response so the caller can branch on status —
 * 201 vs. 200 drives the toast copy ("saved" vs. "version N saved").
 */
export const saveBriefAsDocument = (matterId, { jobId } = {}) => {
  if (!matterId) {
    return Promise.reject(new Error("saveBriefAsDocument: matterId is required"));
  }
  if (!jobId) {
    return Promise.reject(new Error("saveBriefAsDocument: jobId is required"));
  }
  return api.post(`/matters/${matterId}/save-brief/`, { job_id: jobId });
};

// ---------------------------------------------------------------------------
// Brief feedback (Brief-improvement series, 2026-07-23) — attorney
// judgment capture on The Brief's output. Paired with the backend's
// BriefPromptCapture rows, each feedback row completes an
// (input → output → judgment) training triple for the planned
// open-source model fine-tune.
// ---------------------------------------------------------------------------

/**
 * GET /api/analysis-jobs/<job_id>/feedback/
 *
 * Doubles as the rollout-gate probe: the backend returns 403 when the
 * current user isn't on `PV3_BRIEF_FEEDBACK_ALLOWED_EMAILS` (limited
 * to Matthew while the correction workflow is proven out). Callers
 * treat 403 as "hide the feedback UI", not as an error — hence the
 * tolerant validateStatus.
 */
export const listBriefFeedback = (jobId) => {
  if (!jobId) return Promise.resolve(null);
  return api.get(`/analysis-jobs/${jobId}/feedback/`, {
    validateStatus: (status) => status === 200 || status === 403,
  });
};

/**
 * POST /api/analysis-jobs/<job_id>/feedback/
 *
 * Payload: `{ target_kind, rating, comment, correction_text,
 * condition_analysis_id }`. `correction_text` is the attorney's
 * rewrite of what the analysis SHOULD have said — the highest-value
 * training signal. Scope is intentionally omitted (defaults to
 * "case"); firm-wide House-Style promotion stays an admin workflow.
 */
export const createBriefFeedback = (jobId, payload) => {
  if (!jobId) {
    return Promise.reject(new Error("createBriefFeedback: jobId is required"));
  }
  return api.post(`/analysis-jobs/${jobId}/feedback/`, payload);
};

/**
 * PATCH /api/feedback/<feedback_id>/ — owner-only partial update of
 * `rating` / `comment` / `correction_text`.
 */
export const updateBriefFeedback = (feedbackId, payload) => {
  if (!feedbackId) {
    return Promise.reject(
      new Error("updateBriefFeedback: feedbackId is required")
    );
  }
  return api.patch(`/feedback/${feedbackId}/`, payload);
};

// ---------------------------------------------------------------------------
// Context Analysis feedback (2026-08-12) — the Context Analysis sibling
// of the Brief feedback surface. Each row snapshots the analysis run it
// judged on the backend, so a comment survives the in-place refresh.
// ---------------------------------------------------------------------------

/**
 * GET /api/documents/<document_id>/context-analysis/feedback/
 *
 * Doubles as the rollout-gate probe: the backend 403s users without
 * the `ai.context_analysis.feedback` capability (limited to Matthew),
 * and callers treat 403 as "hide the feedback UI" — same contract as
 * `listBriefFeedback`.
 */
export const listContextAnalysisFeedback = (documentId) => {
  if (!documentId) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/context-analysis/feedback/`, {
    validateStatus: (status) => status === 200 || status === 403,
  });
};

/**
 * POST /api/documents/<document_id>/context-analysis/feedback/
 *
 * Payload: `{ rating, comment, correction_text }`. The backend
 * snapshots the current analysis run server-side; nothing about the
 * run needs to be sent from here.
 */
export const createContextAnalysisFeedback = (documentId, payload) => {
  if (!documentId) {
    return Promise.reject(
      new Error("createContextAnalysisFeedback: documentId is required")
    );
  }
  return api.post(`/documents/${documentId}/context-analysis/feedback/`, payload);
};

/**
 * PATCH /api/context-analysis-feedback/<feedback_id>/ — owner-only
 * partial update of `rating` / `comment` / `correction_text`.
 */
export const updateContextAnalysisFeedback = (feedbackId, payload) => {
  if (!feedbackId) {
    return Promise.reject(
      new Error("updateContextAnalysisFeedback: feedbackId is required")
    );
  }
  return api.patch(`/context-analysis-feedback/${feedbackId}/`, payload);
};

// ---------------------------------------------------------------------------
// Brief-scoped correction chat (2026-07-23). Same rollout gate as the
// feedback surface: the list endpoint 403s users not on the allowlist,
// so callers treat 403 as "hide the chat UI".
// ---------------------------------------------------------------------------

/** GET /api/analysis-jobs/<job_id>/chat/ */
export const listBriefChat = (jobId) => {
  if (!jobId) return Promise.resolve(null);
  return api.get(`/analysis-jobs/${jobId}/chat/`, {
    validateStatus: (status) => status === 200 || status === 403,
  });
};

/**
 * POST /api/analysis-jobs/<job_id>/chat/
 *
 * Payload: `{ message, images: [{ mime_type, data_base64 }] }`. Returns
 * `{ user_message, assistant_message, revision }` — `revision` is a
 * proposed Brief correction awaiting apply/dismiss, or null.
 */
export const sendBriefChat = (jobId, payload) => {
  if (!jobId) {
    return Promise.reject(new Error("sendBriefChat: jobId is required"));
  }
  return api.post(`/analysis-jobs/${jobId}/chat/`, payload);
};

/** POST /api/brief-revisions/<id>/apply/ — write the correction into the Brief. */
export const applyBriefRevision = (revisionId) => {
  if (!revisionId) {
    return Promise.reject(
      new Error("applyBriefRevision: revisionId is required")
    );
  }
  return api.post(`/brief-revisions/${revisionId}/apply/`);
};

/** POST /api/brief-revisions/<id>/dismiss/ */
export const dismissBriefRevision = (revisionId) => {
  if (!revisionId) {
    return Promise.reject(
      new Error("dismissBriefRevision: revisionId is required")
    );
  }
  return api.post(`/brief-revisions/${revisionId}/dismiss/`);
};

// ---------------------------------------------------------------------------
// Context Analysis (Phase 5 — per-document, per-matter "fit into matter"
// AI artifact — separate FSM from the deep AnalysisJob above).
// ---------------------------------------------------------------------------

/**
 * GET /api/documents/<int:document_id>/context-analysis/
 *
 * Returns the per-document context analysis row:
 *
 *   {
 *     status: "pending" | "running" | "completed" | "failed",
 *     content_md: string,
 *     recommended_actions: Array<{ action, urgency, rationale }>,
 *     computed_at: string | null,
 *     model_version: string,
 *     error_message: string,
 *   }
 *
 * BE contract notes (handoff §6):
 *   - GET ALWAYS returns 200 even when no row exists yet — in that
 *     case `status="pending"`, `content_md=""`, `computed_at=null`.
 *     Callers should NOT 404-special-case this; the slice treats
 *     the no-row state identically to `pending`.
 *   - Status FSM is `pending → running → completed | failed`. While
 *     pending or running, FE polls every 5s; terminal states stop
 *     polling.
 *   - `recommended_actions` is currently always [] (LLM step is a
 *     scaffold; deterministic stub markdown is returned in
 *     `content_md`). The shape is locked, so the FE renderer
 *     "lights up" automatically once the prompt owner ships the
 *     real LLM swap.
 *   - `model_version` is a debug field — don't display to users.
 *     Use `computed_at` for "Updated N min ago" UX.
 */
export const getDocumentContextAnalysis = (documentId) => {
  if (!documentId) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/context-analysis/`);
};

/**
 * POST /api/documents/<int:document_id>/context-analysis/refresh/
 *
 * Re-enqueues a context analysis run. Returns 202 with the row reset to
 * `status="pending"`. Producer dedupes via upsert, so even an accidental
 * double-click only triggers one effective recompute — but the FE adds
 * a 5s client-side cooldown anyway to keep poll traffic polite.
 *
 * Phase 5b consumer; defined here so the service surface for context
 * analysis lives in one place.
 */
export const refreshDocumentContextAnalysis = (documentId) => {
  if (!documentId) {
    return Promise.reject(
      new Error("refreshDocumentContextAnalysis: documentId is required")
    );
  }
  return api.post(`/documents/${documentId}/context-analysis/refresh/`);
};

// ---------------------------------------------------------------------------
// Markdown extraction (BE PR #504) — feeds the PDF / Formatted / Source
// view-mode toggle in `TabView.js`.
// ---------------------------------------------------------------------------

/**
 * GET /api/documents/<pk>/markdown/
 *
 * Returns `{ id, markdown_content, extracted_at, pipeline_status }` for the
 * extracted markdown of a document. The markdown is served from a dedicated
 * endpoint (rather than the generic detail endpoint) because extracted
 * markdown can be megabytes per document and would bloat every detail
 * payload — the toggle is opt-in, so the cost lands only on tabs that need
 * it.
 *
 * Backend contract (BE PR #504):
 *   - 200 with `markdown_content: ""` while extraction is still in
 *     progress. The toggle gate is truthy on `markdownContentByTab[tab.id]`,
 *     so an empty string keeps the toggle hidden — callers should treat
 *     200-with-empty as "not ready yet, retry on next tab open".
 *   - 200 with non-empty markdown once extraction has landed.
 *   - 404 when the document is missing or soft-deleted. Treated as a
 *     benign "no toggle" state, not a failure (validateStatus tolerates
 *     it so the caller can branch on `response.status` without a try/catch).
 *   - Auth required (`IsAuthenticatedWithJWT`).
 *
 * Returns null without making a request when called with no `documentId`.
 */
export const getDocumentMarkdown = (documentId) => {
  if (!documentId) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/markdown/`, {
    // 404 = soft-deleted / missing doc; not a hard error for the toggle's
    // empty-state UI.
    validateStatus: (status) => status === 200 || status === 404,
  });
};

/**
 * GET /api/documents/<pk>/extracted-search/?q=<term>
 *
 * Searches the document's AI-extracted text (NOT the PDF's embedded
 * text layer, which on scanned/handwritten VA records is garbage OCR
 * that misses text the extraction reads cleanly) and returns
 * page-anchored hits:
 *
 *   `{ query, has_extraction, results: [{ page, snippet, match_count }] }`
 *
 * `page` is 1-based (null for legacy extractions without page
 * markers), so callers can jump the Apryse viewer straight to the hit.
 *
 * Backend contract (BE PR #1794):
 *   - 400 when `q` is shorter than 2 chars — callers should gate
 *     locally and never send those.
 *   - 404 when the document is missing or soft-deleted (tolerated so
 *     the search UI can render an empty state without a try/catch).
 *
 * Returns null without making a request when `documentId` or a usable
 * `query` is missing.
 */
export const searchDocumentExtractedText = (documentId, query) => {
  const trimmed = typeof query === "string" ? query.trim() : "";
  if (!documentId || trimmed.length < 2) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/extracted-search/`, {
    params: { q: trimmed },
    validateStatus: (status) => status === 200 || status === 404,
  });
};

/**
 * Positions of a word in the file the viewer is showing, including words
 * a scanner hid on top of a picture of the page. The viewer's own search
 * does not see that ink.
 */
export const searchDocumentTextLayer = (documentId, query, { wholeWord = false } = {}) => {
  const trimmed = typeof query === "string" ? query.trim() : "";
  if (!documentId || trimmed.length < 2) return Promise.resolve(null);
  return api.get(`/documents/${documentId}/text-layer-search/`, {
    params: { q: trimmed, whole_word: wholeWord ? "1" : "0" },
    validateStatus: (status) => status === 200 || status === 404,
  });
};
